import { notify } from '@/presentation';
import { handleRefreshToken } from '@/business/core/helper/RefreshToken.helper';
import type { AxiosError, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/vue';

const onResponseError = (error: AxiosError) => {
	const config = error.config as { isShowNotify?: boolean };
	const isShowNotify = config?.isShowNotify ?? false;

	if (!error.response) {
		Sentry.captureException(error, {
			tags: { type: 'network-error' },
			extra: {
				url: error.config?.url,
				method: error.config?.method,
			},
		});
		notify({
			message: 'عدم ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید.',
			type: 'error',
		});
		return Promise.reject({
			success: false,
			status: null,
			data: null,
			statusText: error.message,
		});
	}

	const { status, data, statusText } = error.response;
	const payload = { success: false, status, data, statusText };

	if (status >= 500) {
		Sentry.captureException(error, {
			tags: { type: 'server-error' },
			extra: {
				url: error.config?.url,
				method: error.config?.method,
				status,
				response: data,
			},
		});
	}

	if (isShowNotify) {
		const errors = data?.Errors;
		const message = data?.Message;
		if (errors && typeof errors === 'object') {
			for (const key in errors) {
				if (Array.isArray(errors[key])) {
					errors[key].forEach((err: string) => notify({ message: err, type: 'error' }));
				}
			}
		} else if (message) {
			notify({ message, type: 'error' });
		}
	}

	switch (status) {
		case 401:
			handleRefreshToken(error);
			break;

		case 404:
			notify({
				message: data?.Message ?? 'درخواست مورد نظر یافت نشد',
				type: 'error',
			});
			break;

		case 400:
		case 403:
		case 409:
			notify({
				message: data?.Message ?? 'خطایی رخ داده است',
				type: 'error',
			});
			break;

		case 500:
			console.warn('خطای داخلی سرور', data);
			notify({
				message: 'خطایی در سامانه به وجود آمده است.',
				type: 'error',
			});
			break;

		case 502:
			notify({
				message: 'لطفاً صفحه را دوباره بارگذاری کنید یا بعداً تلاش نمایید.',
				type: 'error',
			});
			break;
	}

	return Promise.reject(payload);
};

const onResponse = (response: AxiosResponse) => {
	const config = response.config as { isShowNotify?: boolean };
	const isShowNotify = config.isShowNotify ?? false;
	const message = response?.data?.message;
	if (isShowNotify && message) {
		notify({ message, type: 'success' });
	}
	return Promise.resolve({
		success: true,
		status: response.status,
		data: response.data,
		statusText: response.statusText,
	});
};

export { onResponse, onResponseError };
